import React, { useEffect } from 'react'
import { Forbidden } from 'fejl'
import { observer } from 'mobx-react'
import { RouteChildrenProps } from 'react-router'
import JobRootViewModel from './JobRootViewModel'
import JobList from './JobList'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import { useViewportSize } from 'hooks/viewport-hooks'
import { Breakpoints } from 'consts/breakpoints'
import AccessDeniedView from 'components/AccessDeniedView'
import { SplitView, SplitViewMain, SplitViewSide } from 'components/SplitView'
import JobCards from './Job'
import styles from './JobRoot.sass'

/**
 * Props for the component.
 */
interface Props extends RouteChildrenProps<{ jobId: string }> {
  rootVM: JobRootViewModel
}

/**
 * Job root screen.
 */
export default observer(function JobRoot({ rootVM, match }: Props) {
  // Job ID from the route.
  const id = match?.params?.jobId
  const viewport = useViewportSize()
  const [highlightBorder, setHighlightBorder] = React.useState(false)
  const allowShowingList = viewport.width > Breakpoints.collapseSidebar
  useEffect(() => {
    rootVM.activate()
    return () => rootVM.deactivate()
  }, [rootVM])
  useEffect(() => {
    rootVM.viewJob(id)
  }, [rootVM, id])
  useEffect(() => {
    if (
      rootVM.jobListVM.visible &&
      viewport.width < Breakpoints.autoHideJobList
    ) {
      rootVM.jobListVM.toggleVisibility()
    }
  }, [rootVM.jobListVM, viewport])

  return (
    <div className={styles.container}>
      <div className={styles.fullHeight}>
        <SplitView>
          {allowShowingList && (
            <SplitViewSide
              collapsed={!rootVM.jobListVM.visible}
              highlightBorder={highlightBorder}
            >
              <JobList vm={rootVM.jobListVM} />
            </SplitViewSide>
          )}

          <SplitViewMain>
            {rootVM.viewJob.match({
              pending: () => <ContentLoading />,
              rejected: (err) =>
                err instanceof Forbidden ? (
                  <AccessDeniedView title={err.message} />
                ) : (
                  <ContentError error={err} />
                ),
              resolved: (vm) =>
                vm && (
                  <JobCards
                    vm={vm}
                    onJobListToggleHoverChange={setHighlightBorder}
                    isJobListSidebarVisible={rootVM.jobListVM.visible}
                    onJobListSidebarToggleClick={
                      allowShowingList
                        ? rootVM.jobListVM.toggleVisibility
                        : undefined
                    }
                  />
                ),
            })}
          </SplitViewMain>
        </SplitView>
      </div>
    </div>
  )
})
